import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../../Config';

const GestionLivraisonMateriels = () => {
  const { livraisonId } = useParams();
  const navigate = useNavigate();
  const [livraison, setLivraison] = useState(null);
  const [evenement, setEvenement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [quantitesLivrees, setQuantitesLivrees] = useState({});
  const [activeStep, setActiveStep] = useState(2);
  const [saving, setSaving] = useState(false);
  const [modificationsEnCours, setModificationsEnCours] = useState({});

  useEffect(() => {
    if (livraisonId) {
      chargerDonnees();
    }
  }, [livraisonId]);

  const chargerDonnees = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Charger la livraison avec les détails de l'événement et des matériels
      const response = await axios.get(`${API_URL}/livraisons/${livraisonId}?include=evenement.materiels`, config);
      setLivraison(response.data);
      
      if (response.data.evenement) {
        setEvenement(response.data.evenement);
        
        // Initialiser les quantités livrées
        const initialQuantites = {};
        const modifications = {};
        
        if (response.data.evenement.materiels) {
          response.data.evenement.materiels.forEach(materiel => {
            const key = materiel.id;
            initialQuantites[key] = materiel.pivot?.quantite_livree || 0;
            modifications[key] = false;
          });
        }
        
        setQuantitesLivrees(initialQuantites);
        setModificationsEnCours(modifications);
      }

    } catch (error) {
      console.error('Erreur chargement données:', error);
      
      let errorMessage = 'Erreur lors du chargement des données';
      if (error.response?.status === 403) {
        errorMessage = 'Accès refusé. Vous devez être connecté en tant que magasinier.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Livraison non trouvée.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuantiteChange = (materielId, value) => {
    const materiel = evenement?.materiels?.find(m => m.id === materielId);
    if (!materiel) return;
    
    const quantiteValidee = materiel.pivot?.quantite_validee || 0;
    const stockDisponible = materiel.stock || 0;
    const valeurNumerique = parseInt(value) || 0;
    
    // Limiter la quantité à la quantité validée et au stock disponible
    const maxQuantite = Math.min(quantiteValidee, stockDisponible);
    const quantite = Math.max(0, Math.min(valeurNumerique, maxQuantite));
    
    setQuantitesLivrees(prev => ({
      ...prev,
      [materielId]: quantite
    }));

    // Marquer comme modifié si différent de la valeur actuelle
    const quantiteActuelle = materiel.pivot?.quantite_livree || 0;
    setModificationsEnCours(prev => ({
      ...prev,
      [materielId]: quantite !== quantiteActuelle
    }));
  };

  const handleSaveLivraison = async (materielId) => {
    if (!evenement) return;
    
    const materiel = evenement.materiels?.find(m => m.id === materielId);
    if (!materiel) return;

    const quantiteLivree = quantitesLivrees[materielId] || 0;
    const quantiteActuelle = materiel.pivot?.quantite_livree || 0;
    
    // Vérifier s'il y a un changement
    if (quantiteLivree === quantiteActuelle) {
      setMessage({
        type: 'info',
        text: 'Aucun changement détecté'
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      await axios.patch(
        `${API_URL}/evenements/${evenement.id}/materiels/${materielId}/livraison`,
        { quantite_livree: quantiteLivree },
        config
      );

      setMessage({
        type: 'success',
        text: `Quantité livrée mise à jour: ${quantiteLivree} unité(s)`
      });

      // Réinitialiser le flag de modification
      setModificationsEnCours(prev => ({
        ...prev,
        [materielId]: false
      }));

      // Recharger les données
      chargerDonnees();
    } catch (error) {
      console.error('Erreur mise à jour livraison:', error);
      
      let errorMessage = 'Erreur lors de la mise à jour';
      if (error.response?.status === 403) {
        errorMessage = 'Permission refusée. Action réservée aux magasiniers.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Matériel non trouvé.';
      } else if (error.response?.status === 422) {
        // Erreur de validation
        const errors = error.response.data.errors;
        errorMessage = Object.values(errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    if (!evenement || !evenement.materiels) return;
    
    // Filtrer seulement les matériels modifiés
    const materiauxModifies = evenement.materiels.filter(materiel => {
      const quantiteLivree = quantitesLivrees[materiel.id] || 0;
      const quantiteActuelle = materiel.pivot?.quantite_livree || 0;
      return quantiteLivree !== quantiteActuelle;
    });

    if (materiauxModifies.length === 0) {
      setMessage({
        type: 'info',
        text: 'Aucune modification détectée'
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const promises = materiauxModifies.map(async (materiel) => {
        const quantiteLivree = quantitesLivrees[materiel.id] || 0;
        
        return axios.patch(
          `${API_URL}/evenements/${evenement.id}/materiels/${materiel.id}/livraison`,
          { quantite_livree: quantiteLivree },
          config
        );
      });

      await Promise.all(promises);
      
      setMessage({
        type: 'success',
        text: `${materiauxModifies.length} matériel(s) mis à jour avec succès`
      });

      // Réinitialiser tous les flags de modification
      const resetModifications = {};
      evenement.materiels.forEach(materiel => {
        resetModifications[materiel.id] = false;
      });
      setModificationsEnCours(resetModifications);

      chargerDonnees();
    } catch (error) {
      console.error('Erreur mise à jour globale:', error);
      
      let errorMessage = 'Erreur lors de la mise à jour';
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        errorMessage = Object.values(errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAnnulerModifications = () => {
    if (!evenement || !evenement.materiels) return;
    
    const initialQuantites = {};
    const resetModifications = {};
    
    evenement.materiels.forEach(materiel => {
      const key = materiel.id;
      initialQuantites[key] = materiel.pivot?.quantite_livree || 0;
      resetModifications[key] = false;
    });
    
    setQuantitesLivrees(initialQuantites);
    setModificationsEnCours(resetModifications);
    
    setMessage({
      type: 'info',
      text: 'Modifications annulées'
    });
  };

  const getStatutBadge = (materiel) => {
    const quantiteValidee = materiel.pivot?.quantite_validee || 0;
    const quantiteLivree = materiel.pivot?.quantite_livree || 0;
    const stockDisponible = materiel.stock || 0;
    
    if (quantiteLivree === 0) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Non livré
        </span>
      );
    } else if (quantiteLivree < quantiteValidee) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Partiel
        </span>
      );
    } else if (quantiteLivree === quantiteValidee) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Complet
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Excédent
        </span>
      );
    }
  };

  const getStockDisponibilite = (materiel) => {
    const quantiteValidee = materiel.pivot?.quantite_validee || 0;
    const stock = materiel.stock || 0;
    
    if (stock === 0) {
      return { texte: 'Stock épuisé', classe: 'bg-red-100 text-red-800' };
    } else if (stock < quantiteValidee) {
      return { texte: `Stock bas (${stock})`, classe: 'bg-orange-100 text-orange-800' };
    } else {
      return { texte: `Disponible (${stock})`, classe: 'bg-green-100 text-green-800' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const steps = ['Création', 'Validation', 'Livraison', 'Retour'];

  // Calculer le nombre de modifications en cours
  const nombreModifications = Object.values(modificationsEnCours).filter(Boolean).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!livraison) {
    return (
      <div className="min-h-screen  p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-700">Livraison non trouvée</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/magasinier/livraisons')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover: transition-colors"
          >
            Retour aux livraisons
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestion des Matériels</h1>
              <p className="text-gray-600 mt-1">
                Livraison #{livraison.id} - {evenement ? `Événement #${evenement.id} - ${evenement.beneficiaire?.nom}` : 'Chargement...'}
              </p>
              {nombreModifications > 0 && (
                <div className="mt-2 text-sm text-blue-600 font-medium">
                  {nombreModifications} modification(s) en attente
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/magasinier/livraisons')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover: transition-colors"
              >
                Retour aux livraisons
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Messages */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
            message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
            message.type === 'info' ? 'bg-blue-50 border border-blue-200 text-blue-700' :
            'bg-yellow-50 border border-yellow-200 text-yellow-700'
          }`}>
            <div className="flex items-center">
              <svg className={`w-5 h-5 mr-3 ${
                message.type === 'error' ? 'text-red-500' :
                message.type === 'success' ? 'text-green-500' :
                message.type === 'info' ? 'text-blue-500' : 'text-yellow-500'
              }`} fill="currentColor" viewBox="0 0 20 20">
                {message.type === 'error' ? (
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                ) : message.type === 'success' ? (
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                ) : (
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                )}
              </svg>
              <span>{message.text}</span>
            </div>
          </div>
        )}

        {/* Stepper */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {steps.map((step, index) => (
              <React.Fragment key={step}>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    index < activeStep 
                      ? 'bg-blue-600 text-white' 
                      : index === activeStep 
                      ? 'bg-white border-2 border-blue-600 text-blue-600' 
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {index < activeStep ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    index <= activeStep ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {step}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    index < activeStep ? 'bg-blue-600' : 'bg-gray-200'
                  }`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Informations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations Livraison</h3>
            <div className="space-y-2">
              <div>
                <div className="text-sm text-gray-500">Date</div>
                <div className="font-medium">{formatDate(livraison.date_livraison)}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Transport</div>
                <div className="font-medium">{livraison.transport_inclus ? 'Inclus' : 'Non inclus'}</div>
              </div>
              {livraison.km_avant && (
                <div>
                  <div className="text-sm text-gray-500">Kilométrage avant</div>
                  <div className="font-medium">{livraison.km_avant} km</div>
                </div>
              )}
              {livraison.km_apres && (
                <div>
                  <div className="text-sm text-gray-500">Kilométrage après</div>
                  <div className="font-medium">{livraison.km_apres} km</div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Récapitulatif Matériels</h3>
            <div className="space-y-3">
              {evenement && evenement.materiels ? (
                <>
                  <div className="flex justify-between">
                    <div className="text-sm text-gray-600">Total matériels</div>
                    <div className="font-medium">{evenement.materiels.length}</div>
                  </div>
                  <div className="flex justify-between">
                    <div className="text-sm text-gray-600">Livrés</div>
                    <div className="font-medium text-green-600">
                      {evenement.materiels.filter(m => m.pivot?.quantite_livree > 0).length}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <div className="text-sm text-gray-600">À livrer</div>
                    <div className="font-medium text-yellow-600">
                      {evenement.materiels.filter(m => !m.pivot?.quantite_livree || m.pivot.quantite_livree === 0).length}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <div className="text-sm text-gray-600">Modifications</div>
                    <div className={`font-medium ${nombreModifications > 0 ? 'text-blue-600' : 'text-gray-600'}`}>
                      {nombreModifications}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-gray-500">Aucun matériel</div>
              )}
            </div>
          </div>
        </div>

        {/* Progress bar */}
        {saving && (
          <div className="mb-6">
            <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 animate-pulse"></div>
            </div>
            <div className="text-center text-sm text-gray-600 mt-2">Enregistrement en cours...</div>
          </div>
        )}

        {/* Table des matériels */}
        <div className="bg-white border rounded-lg shadow-sm overflow-hidden mb-8">
          <div className="px-6 py-4 border-b">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Liste des matériels</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Gestion des quantités à livrer
                </p>
              </div>
              {nombreModifications > 0 && (
                <div className="text-sm text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                  {nombreModifications} modification(s) non enregistrée(s)
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Matériel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Validée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Livrée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantité à livrer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {evenement && evenement.materiels && evenement.materiels.length > 0 ? (
                  evenement.materiels.map((materiel) => {
                    const quantiteValidee = materiel.pivot?.quantite_validee || 0;
                    const quantiteLivree = materiel.pivot?.quantite_livree || 0;
                    const quantiteSaisie = quantitesLivrees[materiel.id] || 0;
                    const stockInfo = getStockDisponibilite(materiel);
                    const estModifie = modificationsEnCours[materiel.id] || false;
                    const quantiteMax = Math.min(quantiteValidee, materiel.stock || 0);
                    
                    return (
                      <tr key={materiel.id} className={`hover: transition-colors ${
                        estModifie ? 'bg-blue-50' : ''
                      }`}>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{materiel.nom}</div>
                            <div className="text-xs text-gray-500">{materiel.reference}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stockInfo.classe}`}>
                            {stockInfo.texte}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {quantiteValidee}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            quantiteLivree > 0 ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {quantiteLivree}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {getStatutBadge(materiel)}
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <input
                              type="number"
                              value={quantiteSaisie}
                              onChange={(e) => handleQuantiteChange(materiel.id, e.target.value)}
                              min="0"
                              max={quantiteMax}
                              disabled={saving || quantiteMax === 0}
                              className={`w-24 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:border-blue-500 ${
                                estModifie 
                                  ? 'border-blue-500 bg-blue-50 focus:ring-blue-500' 
                                  : 'border-gray-300 focus:ring-blue-500'
                              } ${(saving || quantiteMax === 0) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                            <div className="text-xs text-gray-500 mt-1">
                              Max: {quantiteMax}
                              {estModifie && (
                                <span className="ml-2 text-blue-600">
                                  ● Modifié
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleSaveLivraison(materiel.id)}
                            disabled={saving || !estModifie}
                            className={`p-2 rounded transition-colors ${
                              saving || !estModifie
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                            }`}
                            title={estModifie ? "Enregistrer la modification" : "Aucune modification"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        {evenement ? 'Aucun matériel à livrer' : 'Chargement des matériels...'}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={chargerDonnees}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover: transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Actualiser
            </button>
            {nombreModifications > 0 && (
              <button
                onClick={handleAnnulerModifications}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover: transition-colors"
              >
                Annuler modifications
              </button>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSaveAll}
              disabled={saving || nombreModifications === 0}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                saving || nombreModifications === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {saving ? 'Enregistrement...' : `Enregistrer (${nombreModifications})`}
            </button>
          </div>
        </div>

        {/* Informations */}
        <div className="mt-8 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <div className="text-sm font-medium text-blue-800">Instructions</div>
                <div className="text-sm text-blue-700 mt-1 space-y-1">
                  <div>• Entrez les quantités réellement livrées pour chaque matériel</div>
                  <div>• La quantité maximale est limitée par le stock disponible et la quantité validée</div>
                  <div>• Le stock sera automatiquement déduit lors de l'enregistrement</div>
                  <div>• Les lignes modifiées sont surlignées en bleu</div>
                </div>
              </div>
            </div>
          </div>

          {evenement && evenement.materiels && evenement.materiels.some(m => 
            (m.pivot?.quantite_validee || 0) > (m.stock || 0)
          ) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-yellow-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <div className="text-sm font-medium text-yellow-800">Attention - Stock insuffisant</div>
                  <div className="text-sm text-yellow-700 mt-1">
                    Certains matériels ont des quantités validées supérieures au stock disponible.
                    Vous devrez peut-être ajuster les quantités à livrer ou réapprovisionner le stock.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GestionLivraisonMateriels;